import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./GanttChart.css";
import API_BASE_URL from "./RestAPI.js";
import {
  FiChevronDown, FiChevronRight, FiZoomIn, FiZoomOut, FiCalendar,
  FiRefreshCw, FiSearch, FiFilter, FiX, FiClock, FiUser
} from "react-icons/fi";
import { GoBug } from "react-icons/go";
import { Tooltip } from "@mui/material";
import FilterDropdown from "./Common Components/FilterDropdown";

/* ───── Constants ───── */
const ZOOM_LEVELS = [
  { label: 'Day', days: 1, colWidth: 40, fmt: 'd' },
  { label: 'Week', days: 7, colWidth: 120, fmt: 'w' },
  { label: 'Month', days: 30, colWidth: 160, fmt: 'm' },
];

const STATUS_COLORS = {
  'To Do': '#64748b', 'Dev In Progress': '#2196f3', 'Dev Completed': '#00bcd4',
  'In Review': '#3f51b5', 'QA In Progress': '#ff9800', 'QA Pass': '#4caf50',
  'QA Fail': '#f44336', 'Done': '#4caf50', 'Closed': '#78909c',
};

const TYPE_COLORS = { Epic: '#7c5cfc', Story: '#2196f3', Bug: '#f44336' };

const getStatusColor = (s) => STATUS_COLORS[s] || '#78909c';
const getTypeColor = (t) => TYPE_COLORS[t] || '#78909c';

/* ───── Date helpers ───── */
const parseDate = (d) => d ? new Date(d) : null;
const daysBetween = (a, b) => Math.round((b - a) / (1000 * 60 * 60 * 24));
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const fmtDate = (d) => d ? `${d.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}` : '';
const fmtDateFull = (d) => d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();

/* ═══════════════ GanttChart Component ═══════════════ */
const GanttChart = () => {
  const [ganttData, setGanttData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoomIdx, setZoomIdx] = useState(0); // day view
  const [expandedSprints, setExpandedSprints] = useState({});
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [hoveredTicket, setHoveredTicket] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const timelineRef = useRef(null);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  };

  /* ───── Fetch data ───── */
  const fetchGanttData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}reports/gantt/data/?status=ALL`, { method: 'GET', headers });
      const data = await res.json();
      if (data.status === 'success') {
        setGanttData(data.data || []);
        // Auto-expand all sprints
        const exp = {};
        (data.data || []).forEach(s => { exp[s.id] = true; });
        setExpandedSprints(exp);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGanttData(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchGanttData();
    setRefreshing(false);
  };

  /* ───── Compute timeline range ───── */
  const { timelineStart, timelineEnd, totalDays, columns } = useMemo(() => {
    if (!ganttData.length) return { timelineStart: new Date(), timelineEnd: new Date(), totalDays: 30, columns: [] };

    let minDate = null, maxDate = null;

    ganttData.forEach(sprint => {
      const sd = parseDate(sprint.start_date);
      const ed = parseDate(sprint.end_date);
      if (sd && (!minDate || sd < minDate)) minDate = sd;
      if (ed && (!maxDate || ed > maxDate)) maxDate = ed;

      (sprint.tickets || []).forEach(t => {
        const ts = parseDate(t.start_date);
        const te = parseDate(t.end_date);
        if (ts && (!minDate || ts < minDate)) minDate = ts;
        if (te && (!maxDate || te > maxDate)) maxDate = te;
      });
    });

    if (!minDate) minDate = new Date();
    if (!maxDate) maxDate = addDays(minDate, 30);

    // Add padding
    const start = addDays(minDate, -2);
    const end = addDays(maxDate, 3);
    const total = Math.max(daysBetween(start, end), 7);

    const zoom = ZOOM_LEVELS[zoomIdx];
    const cols = [];
    let cur = new Date(start);
    while (cur <= end) {
      cols.push(new Date(cur));
      cur = addDays(cur, zoom.days);
    }

    return { timelineStart: start, timelineEnd: end, totalDays: total, columns: cols };
  }, [ganttData, zoomIdx]);

  const zoom = ZOOM_LEVELS[zoomIdx];
  const totalWidth = columns.length * zoom.colWidth;

  /* ───── Position helpers ───── */
  const getLeft = (date) => {
    if (!date) return 0;
    const d = daysBetween(timelineStart, date);
    return (d / totalDays) * totalWidth;
  };

  const getWidth = (start, end) => {
    if (!start || !end) return zoom.colWidth;
    const d = Math.max(daysBetween(start, end), 1);
    return (d / totalDays) * totalWidth;
  };

  /* ───── Today position ───── */
  const today = new Date();
  const todayLeft = getLeft(today);
  const showToday = today >= timelineStart && today <= timelineEnd;

  /* ───── Scroll to today on mount ───── */
  useEffect(() => {
    if (showToday && timelineRef.current) {
      setTimeout(() => {
        timelineRef.current.scrollLeft = Math.max(0, todayLeft - 300);
      }, 300);
    }
  }, [loading, showToday, todayLeft]);

  /* ───── Filter tickets ───── */
  const getFilteredTickets = (tickets) => {
    let filtered = tickets;
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(t =>
        t.code.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.developers || []).some(d => d.toLowerCase().includes(q))
      );
    }
    if (filterType) filtered = filtered.filter(t => t.ticket_type === filterType);
    if (filterStatus) filtered = filtered.filter(t => t.status === filterStatus);
    return filtered;
  };

  /* ───── Unique values for filters ───── */
  const { allTypes, allStatuses } = useMemo(() => {
    const types = new Set();
    const statuses = new Set();
    ganttData.forEach(s => (s.tickets || []).forEach(t => {
      if (t.ticket_type) types.add(t.ticket_type);
      if (t.status) statuses.add(t.status);
    }));
    return { allTypes: [...types], allStatuses: [...statuses] };
  }, [ganttData]);

  const hasFilters = search || filterType || filterStatus;

  /* ───── Toggle sprint expand ───── */
  const toggleSprint = (id) => {
    setExpandedSprints(prev => ({ ...prev, [id]: !prev[id] }));
  };

  /* ═══════════════ RENDER ═══════════════ */
  if (loading) {
    return (
      <div className="gantt-page">
        <div className="gantt-loading"><div className="gantt-loading__spinner" /><span>Loading Gantt chart...</span></div>
      </div>
    );
  }

  return (
    <div className="gantt-page">
      {/* ── Toolbar ── */}
      <div className="gantt-toolbar">
        <div className="gantt-toolbar__left">
          <h1 className="gantt-toolbar__title"><FiCalendar size={18} /> Gantt Chart</h1>
          <div className="gantt-zoom">
            <button className="gantt-zoom__btn" disabled={zoomIdx === 0} onClick={() => setZoomIdx(Math.max(0, zoomIdx - 1))} title="Zoom in"><FiZoomIn size={14} /></button>
            <span className="gantt-zoom__label">{zoom.label}</span>
            <button className="gantt-zoom__btn" disabled={zoomIdx === ZOOM_LEVELS.length - 1} onClick={() => setZoomIdx(Math.min(ZOOM_LEVELS.length - 1, zoomIdx + 1))} title="Zoom out"><FiZoomOut size={14} /></button>
          </div>
        </div>
        <div className="gantt-toolbar__right">
          <div className="gantt-toolbar__search">
            <FiSearch size={12} />
            <input type="text" placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <FilterDropdown label="Type" value={filterType} options={allTypes} onChange={setFilterType} />
          <FilterDropdown label="Status" value={filterStatus} options={allStatuses} onChange={setFilterStatus} />
          {hasFilters && (
            <button className="gantt-toolbar__clear" onClick={() => { setSearch(''); setFilterType(''); setFilterStatus(''); }}>
              <FiX size={12} /> Clear
            </button>
          )}
          <button className={`gantt-toolbar__refresh ${refreshing ? 'gantt-toolbar__refresh--spin' : ''}`} onClick={handleRefresh}>
            <FiRefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* ── Gantt Body ── */}
      {ganttData.length === 0 ? (
        <div className="gantt-empty"><FiCalendar size={36} /><p>No sprints found</p></div>
      ) : (
        <div className="gantt-container">
          {/* Left panel: labels */}
          <div className="gantt-labels">
            <div className="gantt-labels__header">
              <span>Sprint / Ticket</span>
            </div>
            <div className="gantt-labels__body">
              {ganttData.map(sprint => {
                const isExp = expandedSprints[sprint.id];
                const tickets = getFilteredTickets(sprint.tickets || []);
                return (
                  <React.Fragment key={sprint.id}>
                    <div className="gantt-label gantt-label--sprint" onClick={() => toggleSprint(sprint.id)}>
                      {isExp ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
                      <span className="gantt-label__name">{sprint.sprint_name}</span>
                      <span className="gantt-label__count">{tickets.length}</span>
                    </div>
                    {isExp && tickets.map(ticket => (
                      <div
                        key={ticket.id}
                        className={`gantt-label gantt-label--ticket ${hoveredTicket === ticket.id ? 'gantt-label--hovered' : ''}`}
                        onMouseEnter={() => setHoveredTicket(ticket.id)}
                        onMouseLeave={() => setHoveredTicket(null)}
                      >
                        <span className="gantt-label__type-dot" style={{ background: getTypeColor(ticket.ticket_type) }} />
                        <span className="gantt-label__code">{ticket.code}</span>
                        <span className="gantt-label__desc">{ticket.description}</span>
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Right panel: timeline */}
          <div className="gantt-timeline" ref={timelineRef}>
            {/* Timeline header */}
            <div className="gantt-timeline__header" style={{ width: totalWidth }}>
              {columns.map((col, i) => (
                <div
                  key={i}
                  className={`gantt-col-header ${isSameDay(col, today) ? 'gantt-col-header--today' : ''}`}
                  style={{ width: zoom.colWidth, minWidth: zoom.colWidth }}
                >
                  {zoom.fmt === 'd' && fmtDate(col)}
                  {zoom.fmt === 'w' && `${fmtDate(col)} – ${fmtDate(addDays(col, 6))}`}
                  {zoom.fmt === 'm' && col.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
              ))}
            </div>

            {/* Timeline body */}
            <div className="gantt-timeline__body" style={{ width: totalWidth }}>
              {/* Grid columns */}
              <div className="gantt-grid" style={{ width: totalWidth }}>
                {columns.map((col, i) => (
                  <div
                    key={i}
                    className={`gantt-grid__col ${isSameDay(col, today) ? 'gantt-grid__col--today' : ''}`}
                    style={{ width: zoom.colWidth, minWidth: zoom.colWidth }}
                  />
                ))}
              </div>

              {/* Today line */}
              {showToday && (
                <div className="gantt-today-line" style={{ left: todayLeft }}>
                  <div className="gantt-today-line__dot" />
                </div>
              )}

              {/* Bars */}
              {ganttData.map(sprint => {
                const isExp = expandedSprints[sprint.id];
                const tickets = getFilteredTickets(sprint.tickets || []);
                const sprintStart = parseDate(sprint.start_date);
                const sprintEnd = parseDate(sprint.end_date);

                return (
                  <React.Fragment key={sprint.id}>
                    {/* Sprint bar */}
                    <div className="gantt-row gantt-row--sprint">
                      {sprintStart && sprintEnd && (
                        <div
                          className="gantt-bar gantt-bar--sprint"
                          style={{
                            left: getLeft(sprintStart),
                            width: getWidth(sprintStart, sprintEnd),
                          }}
                        >
                          <span className="gantt-bar__sprint-label">{sprint.sprint_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Ticket bars */}
                    {isExp && tickets.map(ticket => {
                      const tStart = parseDate(ticket.start_date);
                      const tEnd = parseDate(ticket.end_date) || tStart;
                      const barColor = getStatusColor(ticket.status);

                      return (
                        <div
                          key={ticket.id}
                          className={`gantt-row gantt-row--ticket ${hoveredTicket === ticket.id ? 'gantt-row--hovered' : ''}`}
                          onMouseEnter={() => setHoveredTicket(ticket.id)}
                          onMouseLeave={() => setHoveredTicket(null)}
                        >
                          {tStart && (
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <div className="gantt-tooltip">
                                  <div className="gantt-tooltip__header">
                                    <span className="gantt-tooltip__code">{ticket.code}</span>
                                    <span className="gantt-tooltip__status" style={{ background: barColor }}>{ticket.status}</span>
                                  </div>
                                  <div className="gantt-tooltip__desc">{ticket.description}</div>
                                  <div className="gantt-tooltip__meta">
                                    <span><FiClock size={10} /> {fmtDateFull(tStart)} → {fmtDateFull(tEnd)}</span>
                                    {ticket.developers?.length > 0 && <span><FiUser size={10} /> {ticket.developers.join(', ')}</span>}
                                    <span>Est: {ticket.dev_est_hours}h dev + {ticket.qa_est_hours}h qa</span>
                                    <span>Priority: {ticket.priority}</span>
                                  </div>
                                  {ticket.phases?.length > 0 && (
                                    <div className="gantt-tooltip__phases">
                                      <strong>Timeline:</strong>
                                      {ticket.phases.slice(-5).map((p, i) => (
                                        <div key={i} className="gantt-tooltip__phase">
                                          {p.from || 'Created'} → {p.to} <span className="gantt-tooltip__phase-date">{p.date ? fmtDateFull(new Date(p.date)) : ''}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              }
                            >
                              <div
                                className="gantt-bar gantt-bar--ticket"
                                style={{
                                  left: getLeft(tStart),
                                  width: Math.max(getWidth(tStart, tEnd), 8),
                                  background: barColor,
                                }}
                              >
                                {getWidth(tStart, tEnd) > 60 && (
                                  <span className="gantt-bar__ticket-label">{ticket.code}</span>
                                )}
                                {ticket.ticket_type === 'Bug' && <GoBug size={10} className="gantt-bar__bug-icon" />}
                              </div>
                            </Tooltip>
                          )}
                          {!tStart && (
                            <div className="gantt-bar gantt-bar--no-date" style={{ left: getLeft(parseDate(ticket.created_date) || timelineStart) }}>
                              <span className="gantt-bar__ticket-label">{ticket.code} (no activity)</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Legend ── */}
      <div className="gantt-legend">
        <span className="gantt-legend__title">Status:</span>
        {Object.entries(STATUS_COLORS).map(([label, color]) => (
          <span key={label} className="gantt-legend__item">
            <span className="gantt-legend__dot" style={{ background: color }} />
            {label}
          </span>
        ))}
        <span className="gantt-legend__divider" />
        <span className="gantt-legend__title">Type:</span>
        {Object.entries(TYPE_COLORS).map(([label, color]) => (
          <span key={label} className="gantt-legend__item">
            <span className="gantt-legend__dot" style={{ background: color }} />
            {label}
          </span>
        ))}
        {showToday && (
          <>
            <span className="gantt-legend__divider" />
            <span className="gantt-legend__item"><span className="gantt-legend__dot gantt-legend__dot--today" /> Today</span>
          </>
        )}
      </div>
    </div>
  );
};

export default GanttChart;
